import React, { useState, useEffect, useCallback } from "react";
import apiClient from "@/api/client";
import { ChevronLeft, ChevronRight, ClipboardList, Clock, Users } from "lucide-react";

const MONTH_NAMES = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const PrestationsDashboardPage = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);

  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1;

  const fetchData = useCallback(async () => {
    try {
      const res = await apiClient.get(`/prestations/dashboard?year=${year}&month=${month}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
    }
  }, [year, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (isCurrentMonth) return;
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  return (
    <div className="crm-page" data-testid="prestations-dashboard">
      <div className="crm-page-header">
        <div>
          <h1 className="crm-page-title">Prestations Coachs</h1>
          <p className="crm-page-subtitle">Récapitulatif mensuel des prestations</p>
        </div>
      </div>

      {/* Month selector */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <button onClick={prevMonth} className="p-2 rounded-lg hover:bg-[#edf0f4] transition-colors" data-testid="dash-prev-month">
          <ChevronLeft size={20} className="text-[#577083]" />
        </button>
        <span className="text-lg font-semibold text-[#162d3d] min-w-[180px] text-center" data-testid="dash-period">
          {MONTH_NAMES[month - 1]} {year}
        </span>
        <button onClick={nextMonth} disabled={isCurrentMonth} className={`p-2 rounded-lg transition-colors ${isCurrentMonth ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[#edf0f4]'}`} data-testid="dash-next-month">
          <ChevronRight size={20} className="text-[#577083]" />
        </button>
      </div>

      {/* Totals */}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-[#edf0f4] p-4 text-center shadow-sm">
              <div className="text-3xl font-bold text-[#3899ec]" data-testid="total-coaches">{data.totals.coaches_count}</div>
              <div className="text-xs text-[#577083] mt-1">Coachs</div>
            </div>
            <div className="bg-white rounded-xl border border-[#edf0f4] p-4 text-center shadow-sm">
              <div className="text-3xl font-bold text-[#162d3d]" data-testid="total-shifts">{data.totals.shifts}</div>
              <div className="text-xs text-[#577083] mt-1">Shifts totaux</div>
            </div>
            <div className="bg-white rounded-xl border border-[#edf0f4] p-4 text-center shadow-sm">
              <div className="text-3xl font-bold text-[#22c55e]" data-testid="total-cours">{data.totals.cours_collectifs}</div>
              <div className="text-xs text-[#577083] mt-1">Cours collectifs</div>
            </div>
            <div className="bg-[#1a2744] rounded-xl p-4 text-center shadow-sm">
              <div className="text-3xl font-bold text-[#e8d44d]" data-testid="total-heures">{data.totals.heures}h</div>
              <div className="text-xs text-white/70 mt-1">Total heures</div>
            </div>
          </div>

          {/* Table */}
          <div className="crm-card">
            {data.prestations.length === 0 ? (
              <div className="text-center py-10 text-[#8fa4b5]">
                <ClipboardList size={40} className="mx-auto mb-3 opacity-30" />
                <p>Aucune prestation encodée pour {MONTH_NAMES[month - 1]} {year}</p>
              </div>
            ) : (
              <div className="crm-table-wrap" style={{boxShadow:'none'}}>
                <table className="crm-table">
                  <thead>
                    <tr>
                      <th>Coach</th>
                      <th className="text-center">Shifts</th>
                      <th className="text-center">Heures totales</th>
                      <th className="text-center">Cours collectifs</th>
                      <th className="text-center">H. supplémentaires</th>
                      <th className="text-center">Total heures</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.prestations.map((p, i) => (
                      <tr key={i} data-testid={`prestation-row-${i}`}>
                        <td className="font-medium text-[#162d3d]">{p.coach_name}</td>
                        <td className="text-center">{p.shifts}</td>
                        <td className="text-center text-[#577083]">{(p.shifts * 4) + (p.cours_collectifs * 2)}h</td>
                        <td className="text-center">{p.cours_collectifs}</td>
                        <td className="text-center">{p.heures_supplementaires}h</td>
                        <td className="text-center font-semibold text-[#1a2744]">{p.total_heures}h</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-[#f4f7fa] font-semibold">
                      <td className="text-[#162d3d]">TOTAL</td>
                      <td className="text-center">{data.totals.shifts}</td>
                      <td className="text-center text-[#577083]">{(data.totals.shifts * 4) + (data.totals.cours_collectifs * 2)}h</td>
                      <td className="text-center">{data.totals.cours_collectifs}</td>
                      <td className="text-center">{(data.totals.heures - (data.totals.shifts * 4) - (data.totals.cours_collectifs * 2)).toFixed(1)}h</td>
                      <td className="text-center text-[#1a2744]">{data.totals.heures}h</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PrestationsDashboardPage;
